import { useCallback, useEffect, useMemo, useState } from "react"
import adminApi from "../adminApi"
import "./AdminRequests.css"

const statusLabels = {
    new: "Новая",
    in_work: "В работе",
    closed: "Закрыта",
}

const deliveryStatusLabels = {
    not_started: "Сборка не запущена",
    building: "Сборка выполняется",
    pending: "Ожидает сборки",
    processing: "Сборка выполняется",
    ready: "Пакет готов",
    done: "Пакет готов",
    completed: "Пакет готов",
    success: "Пакет готов",
    error: "Ошибка сборки",
    failed: "Ошибка сборки",
}

function formatDate(value) {
    if (!value) return "—"

    return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(value))
}

function sortRequests(requests) {
    return [...requests].sort((a, b) => {
        const dateDiff = new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        if (dateDiff !== 0) return dateDiff

        return Number(b.key ?? 0) - Number(a.key ?? 0)
    })
}

function isDeliveryReady(status) {
    return ["ready", "done", "completed", "success", "готово"].includes(String(status || "").toLowerCase())
}

function getDeliveryLabel(status) {
    if (!status) return "Сборка не запущена"

    return deliveryStatusLabels[status] || status
}

export default function AdminRequests({ apiKey }) {
    const [requests, setRequests] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [updatingKey, setUpdatingKey] = useState(null)
    const [buildModalRequest, setBuildModalRequest] = useState(null)
    const [licenseFile, setLicenseFile] = useState(null)
    const [offlineBuild, setOfflineBuild] = useState(true)
    const [buildSubmitting, setBuildSubmitting] = useState(false)
    const [selectedRequest, setSelectedRequest] = useState(null)

    const sortedRequests = useMemo(() => sortRequests(requests), [requests])

    const updateRequestInState = useCallback((updatedRequest) => {
        if (!updatedRequest) return

        setRequests((currentRequests) => currentRequests.map((request) => (
            request.key === updatedRequest.key ? updatedRequest : request
        )))
    }, [])

    const updateDeliveryFields = useCallback((requestKey, deliveryStatus) => {
        setRequests((currentRequests) => currentRequests.map((request) => {
            if (request.key !== requestKey) return request

            return {
                ...request,
                delivery_order_id: deliveryStatus.order_id ?? request.delivery_order_id,
                delivery_status: deliveryStatus.status ?? request.delivery_status,
                delivery_error: deliveryStatus.error ?? "",
                delivery_updated_at: new Date().toISOString(),
            }
        }))
    }, [])

    const fetchRequests = useCallback(async () => {
        setLoading(true)
        setError("")

        try {
            const response = await adminApi.get(`${apiKey}/requests/`)
            setRequests(response.data.data ?? [])
        } catch (fetchError) {
            setError(fetchError.response?.data?.message || fetchError.message || "Не удалось загрузить заявки")
        } finally {
            setLoading(false)
        }
    }, [apiKey])

    useEffect(() => {
        fetchRequests()
    }, [fetchRequests])

    const updateStatus = async (requestKey, status) => {
        setUpdatingKey(requestKey)
        setError("")

        try {
            const response = await adminApi.put(`${apiKey}/requests/${requestKey}`, { status })
            updateRequestInState(response.data.data)
        } catch (updateError) {
            setError(updateError.response?.data?.message || updateError.message || "Не удалось обновить заявку")
        } finally {
            setUpdatingKey(null)
        }
    }

    const openBuildModal = (request) => {
        setBuildModalRequest(request)
        setLicenseFile(null)
        setOfflineBuild(true)
        setError("")
    }

    const closeBuildModal = () => {
        if (buildSubmitting) return

        setBuildModalRequest(null)
        setLicenseFile(null)
    }

    const createBuild = async () => {
        if (!buildModalRequest || !licenseFile) {
            setError("Прикрепи файл лицензии перед запуском сборки")
            return
        }

        setBuildSubmitting(true)
        setError("")

        const formData = new FormData()
        formData.append("license", licenseFile)
        formData.append("offline", String(offlineBuild))

        try {
            const response = await adminApi.post(`${apiKey}/requests/${buildModalRequest.key}/delivery`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })

            updateRequestInState(response.data.data)
            setBuildModalRequest(null)
            setLicenseFile(null)
        } catch (buildError) {
            setError(buildError.response?.data?.message || buildError.message || "Не удалось запустить сборку")
        } finally {
            setBuildSubmitting(false)
        }
    }

    const refreshBuildStatus = async (requestKey) => {
        setUpdatingKey(requestKey)
        setError("")

        try {
            const response = await adminApi.get(`${apiKey}/requests/${requestKey}/delivery/status`)
            updateDeliveryFields(requestKey, response.data.data ?? {})
        } catch (statusError) {
            setError(statusError.response?.data?.message || statusError.message || "Не удалось обновить статус сборки")
        } finally {
            setUpdatingKey(null)
        }
    }

    const downloadBuild = (requestKey) => {
        window.location.href = `${apiKey}/requests/${requestKey}/delivery/download`
    }

    return (
        <section className="adminRequests">
            <div className="adminRequests__header">
                <div>
                    <h1>Заявки</h1>
                    <p>Компактный список обращений. Нажмите на строку, чтобы открыть детали и действия.</p>
                </div>
                <button className="adminRequests__refresh" type="button" onClick={fetchRequests} disabled={loading}>
                    Обновить
                </button>
            </div>

            {error && <div className="adminRequests__error">{error}</div>}
            {loading && <div className="adminRequests__empty">Загружаем заявки...</div>}
            {!loading && sortedRequests.length === 0 && <div className="adminRequests__empty">Заявок пока нет</div>}

            <div className="adminRequests__list">
                {sortedRequests.map((request) => {
                    const status = request.status || "new"
                    const deliveryStatus = request.delivery_status || "not_started"
                    return (
                        <button
                            className="adminRequests__row"
                            type="button"
                            key={request.key}
                            onClick={() => setSelectedRequest(request)}
                        >
                            <span className="adminRequests__rowMain">
                                <strong>{request.organization || request.email || "Без названия"}</strong>
                                <small>{request.type || "Заявка"}</small>
                            </span>
                            <span className="adminRequests__rowEmail">{request.email}</span>
                            <span className="adminRequests__rowDate">{formatDate(request.created_at)}</span>
                            <span className={`adminRequests__status adminRequests__status_${status}`}>
                                {statusLabels[status] || status}
                            </span>
                            <span className="adminRequests__deliveryBadge">{getDeliveryLabel(deliveryStatus)}</span>
                            <span className="adminRequests__rowArrow" aria-hidden="true">→</span>
                        </button>
                    )
                })}
            </div>

            {selectedRequest && (() => {
                const request = requests.find((item) => item.key === selectedRequest.key) || selectedRequest
                const isUpdating = updatingKey === request.key
                const status = request.status || "new"
                const deliveryStatus = request.delivery_status || "not_started"
                const deliveryReady = isDeliveryReady(deliveryStatus)
                return (
                    <div className="adminRequests__drawerOverlay" role="presentation" onMouseDown={() => setSelectedRequest(null)}>
                        <aside className="adminRequests__drawer" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
                            <button className="adminRequests__drawerClose" type="button" onClick={() => setSelectedRequest(null)} aria-label="Закрыть">×</button>
                            <div className="adminRequests__drawerHeader">
                                <span className="adminRequests__type">{request.type || "Заявка"}</span>
                                <h2>{request.organization || request.email || "Без названия"}</h2>
                                <span className={`adminRequests__status adminRequests__status_${status}`}>{statusLabels[status] || status}</span>
                            </div>
                            <dl className="adminRequests__meta">
                                <div><dt>Email</dt><dd><a href={`mailto:${request.email}`}>{request.email}</a></dd></div>
                                <div><dt>Создана</dt><dd>{formatDate(request.created_at)}</dd></div>
                                <div><dt>Обновлена</dt><dd>{formatDate(request.updated_at)}</dd></div>
                                {request.closed_at && <div><dt>Закрыта</dt><dd>{formatDate(request.closed_at)}</dd></div>}
                            </dl>
                            <div className="adminRequests__message">
                                <span>Сообщение</span>
                                <pre>{request.message || "—"}</pre>
                            </div>
                            <div className={`adminRequests__delivery adminRequests__delivery_${deliveryStatus}`}>
                                <div><span>Сборка поставки</span><strong>{getDeliveryLabel(deliveryStatus)}</strong></div>
                                {request.delivery_order_id && <p>Order ID: {request.delivery_order_id}</p>}
                                {request.delivery_error && <p className="adminRequests__deliveryError">{request.delivery_error}</p>}
                                {request.delivery_updated_at && <p>Обновлено: {formatDate(request.delivery_updated_at)}</p>}
                            </div>
                            <div className="adminRequests__actions">
                                <button type="button" onClick={() => updateStatus(request.key, "in_work")} disabled={isUpdating || status === "in_work" || status === "closed"}>▶ <span>В работу</span></button>
                                <button type="button" onClick={() => updateStatus(request.key, "closed")} disabled={isUpdating || status === "closed"}>✓ <span>Закрыть</span></button>
                                <button type="button" onClick={() => openBuildModal(request)} disabled={isUpdating || buildSubmitting}>⚙ <span>Собрать билд</span></button>
                                {request.delivery_order_id && <button type="button" onClick={() => refreshBuildStatus(request.key)} disabled={isUpdating}>↻ <span>Статус</span></button>}
                                {deliveryReady && <button type="button" onClick={() => downloadBuild(request.key)} disabled={isUpdating}>↓ <span>Скачать</span></button>}
                            </div>
                        </aside>
                    </div>
                )
            })()}

            {buildModalRequest && (
                <div className="adminRequests__modalOverlay" role="presentation" onMouseDown={closeBuildModal}>
                    <div className="adminRequests__modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
                        <h2>Подтвердить сборку билда</h2>
                        <p>Заявка #{buildModalRequest.key}: {buildModalRequest.organization || buildModalRequest.email || "без названия"}</p>
                        <label className="adminRequests__fileField"><span>Файл лицензии</span><input type="file" accept=".json,.lic,application/json" onChange={(event) => setLicenseFile(event.target.files?.[0] ?? null)} /></label>
                        <label className="adminRequests__checkboxField"><input type="checkbox" checked={offlineBuild} onChange={(event) => setOfflineBuild(event.target.checked)} /><span>Собрать offline-поставку</span></label>
                        <div className="adminRequests__modalActions">
                            <button type="button" onClick={closeBuildModal} disabled={buildSubmitting}>Отмена</button>
                            <button type="button" onClick={createBuild} disabled={buildSubmitting || !licenseFile}>{buildSubmitting ? "Запускаем..." : "Подтвердить"}</button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}
